import React from 'react';
import {
  Title2,
  Text,
  Button,
  Caption1,
  Badge,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogActions,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Edit20Regular, Delete20Regular } from '@fluentui/react-icons';
import { useEnvironment } from '../../contexts/AppContext';
import { useMsalAuth } from '../../hooks/useMsalAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BackendsService, ResourcePoolsService, initializeTRAPIClient } from '../../services/api';
import { getErrorMessage } from '../../utils/errors';
import { ExpandableTable } from '../../components/ExpandableTable';
import type { Column } from '../../components/ExpandableTable';
import DetailsFlyout from '../../components/DetailsFlyout';
import ResourcePoolForm from '../../components/forms/ResourcePoolForm';
import type { ResourcePoolObject } from '../../types/trapi';

const useStyles = makeStyles({
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: tokens.spacingVerticalM },
  detail: { display: 'grid', gridTemplateColumns: '120px 1fr', gap: tokens.spacingVerticalXS, alignItems: 'start', '@media (max-width: 600px)': { gridTemplateColumns: '1fr' } },
  label: { color: tokens.colorNeutralForeground3 },
  errorText: { color: tokens.colorPaletteRedForeground1 },
  errorBlock: { color: tokens.colorPaletteRedForeground1, display: 'block', marginBottom: tokens.spacingVerticalS },
  expandedContent: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  badgeRow: { display: 'flex', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
  deleteGrid: { display: 'grid', gap: tokens.spacingVerticalSNudge },
});

export default function ResourcePoolsPage() {
  const styles = useStyles();
  const { currentEnvironment } = useEnvironment();
  const { getToken, account } = useMsalAuth();
  const qc = useQueryClient();

  React.useEffect(() => { initializeTRAPIClient({ environment: currentEnvironment }); }, [currentEnvironment]);

  const query = useQuery<ResourcePoolObject[]>({
    queryKey: ['admin', 'pools', currentEnvironment],
    queryFn: async () => {
      const token = await getToken();
      const result = await ResourcePoolsService.getResourcePools({ environment: currentEnvironment, accessToken: token });
      return Array.isArray(result) ? result : [];
    },
    enabled: !!account,
    staleTime: 60_000,
  });

  const backendsQuery = useQuery<string[]>({
    queryKey: ['admin', 'backends', currentEnvironment, 'names-for-pools'],
    queryFn: async () => {
      const token = await getToken();
      const list = await BackendsService.getBackends({ environment: currentEnvironment, accessToken: token });
      return list.map((b: { name: string }) => b.name);
    },
    enabled: !!account,
    staleTime: 60_000,
  });

  const allItems = query.data ?? [];
  const existingNames = React.useMemo(() => allItems.map(p => p.name), [allItems]);

  // Flyout
  const [flyoutOpen, setFlyoutOpen] = React.useState(false);
  const [flyoutMode, setFlyoutMode] = React.useState<'create' | 'edit'>('create');
  const [editing, setEditing] = React.useState<ResourcePoolObject | null>(null);

  // Delete
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [target, setTarget] = React.useState<ResourcePoolObject | null>(null);

  // Compute used backends (excluding the one being edited)
  const usedBackends = React.useMemo(() => {
    const all = new Set<string>();
    allItems.forEach(p => {
      if (flyoutMode === 'edit' && editing && p.id === editing.id) return;
      (p.backends || []).forEach(b => all.add(b));
    });
    return all;
  }, [allItems, flyoutMode, editing]);

  const availableBackendOptions = React.useMemo(() => {
    return (backendsQuery.data || []).filter(name => !usedBackends.has(name));
  }, [backendsQuery.data, usedBackends]);

  const excludedBackends = React.useMemo(() => {
    const available = new Set(availableBackendOptions);
    return (backendsQuery.data || []).filter(name => !available.has(name));
  }, [backendsQuery.data, availableBackendOptions]);

  const createMut = useMutation({
    mutationFn: async (data: Omit<ResourcePoolObject, 'id' | 'metadata'>) => {
      const token = await getToken();
      return ResourcePoolsService.createResourcePool({ environment: currentEnvironment, accessToken: token, data });
    },
    onSuccess: async () => {
      setFlyoutOpen(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'pools', currentEnvironment] }),
        qc.invalidateQueries({ queryKey: ['pools', 'stats', currentEnvironment] }),
      ]);
    },
    onError: (error: unknown) => { console.error('Create pool failed:', getErrorMessage(error)); },
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Omit<ResourcePoolObject, 'id' | 'metadata'>> }) => {
      const token = await getToken();
      return ResourcePoolsService.updateResourcePool({ environment: currentEnvironment, accessToken: token, id, data });
    },
    onSuccess: async () => {
      setFlyoutOpen(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'pools', currentEnvironment] }),
        qc.invalidateQueries({ queryKey: ['pools', 'stats', currentEnvironment] }),
      ]);
    },
    onError: (error: unknown) => { console.error('Update pool failed:', getErrorMessage(error)); },
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      const token = await getToken();
      return ResourcePoolsService.deleteResourcePool({ id, environment: currentEnvironment, accessToken: token });
    },
    onSuccess: async () => {
      setDeleteOpen(false); setTarget(null);
      await qc.invalidateQueries({ queryKey: ['admin', 'pools', currentEnvironment] });
    },
    onError: (error: unknown) => { console.error('Delete pool failed:', getErrorMessage(error)); },
  });

  const isFormLoading = createMut.isPending || updateMut.isPending;

  const handleFormSubmit = async (data: Omit<ResourcePoolObject, 'id' | 'metadata'>) => {
    const invalid = data.backends.filter(b => usedBackends.has(b));
    if (invalid.length > 0) return;
    if (flyoutMode === 'create') await createMut.mutateAsync(data);
    else if (editing?.id) await updateMut.mutateAsync({ id: editing.id, data });
  };

  const openCreate = () => { setEditing(null); setFlyoutMode('create'); setFlyoutOpen(true); };
  const openEdit = (p: ResourcePoolObject) => { setEditing(p); setFlyoutMode('edit'); setFlyoutOpen(true); };

  const columns: Column<ResourcePoolObject>[] = React.useMemo(() => [
    { key: 'name', label: 'Name', render: (p) => <Text weight="semibold">{p.name}</Text>, getValue: (p) => p.name, sortable: true },
    { key: 'description', label: 'Description', render: (p) => <Caption1 title={p.description} style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{p.description || '-'}</Caption1>, getValue: (p) => p.description || '', sortable: true, hideOnMobile: true },
    { key: 'backends', label: 'Backends', render: (p) => String(p.backends?.length ?? 0), getValue: (p) => String(p.backends?.length ?? 0), sortable: true },
    { key: 'owner', label: 'Owner', render: (p) => p.ownership?.owner || '-', getValue: (p) => p.ownership?.owner || '', sortable: true, filterable: true, hideOnMobile: true },
  ], []);

  const renderExpanded = (p: ResourcePoolObject) => (
    <div className={styles.expandedContent}>
      <div className={styles.buttonRow}>
        <Button size="small" appearance="subtle" icon={<Edit20Regular />} onClick={(e) => { e.stopPropagation(); openEdit(p); }} aria-label="Edit pool">Edit</Button>
        <Button size="small" appearance="outline" icon={<Delete20Regular />}
          style={{ color: tokens.colorPaletteRedForeground1, borderColor: tokens.colorPaletteRedForeground1 }}
          onClick={(e) => { e.stopPropagation(); setTarget(p); setDeleteOpen(true); }} aria-label="Delete pool">Delete</Button>
      </div>
      <div className={styles.detail}>
        <Text className={styles.label}>Backends</Text>
        <div className={styles.badgeRow}>
          {p.backends?.length ? p.backends.map(b => <Badge key={b} appearance="outline">{b}</Badge>) : <Caption1>None</Caption1>}
        </div>
        {p.ownership?.owner && <><Text className={styles.label}>Owner</Text><Text>{p.ownership.owner}</Text></>}
        {p.ownership?.team && <><Text className={styles.label}>Team</Text><Text>{p.ownership.team}</Text></>}
        {p.ownership?.contact && <><Text className={styles.label}>Contact</Text><Text>{p.ownership.contact}</Text></>}
        {p.description && <><Text className={styles.label}>Description</Text><Text>{p.description}</Text></>}
      </div>
    </div>
  );

  return (
    <div>
      <div className={styles.header}>
        <Title2>Resource Pools</Title2>
        <Button appearance="primary" onClick={openCreate}>New Pool</Button>
      </div>

      {query.error ? (
        <Text className={styles.errorText}>{getErrorMessage(query.error)}</Text>
      ) : (
        <ExpandableTable<ResourcePoolObject>
          columns={columns}
          items={allItems}
          getRowKey={(p) => p.id || p.name}
          renderExpanded={renderExpanded}
          loading={query.isLoading}
          emptyMessage="No resource pools configured."
          searchPlaceholder="Search resource pools…"
          defaultSortKey="name"
        />
      )}

      <DetailsFlyout
        open={flyoutOpen}
        onClose={() => setFlyoutOpen(false)}
        title={flyoutMode === 'create' ? 'Create Resource Pool' : 'Edit Resource Pool'}
        size="medium"
        footer={
          <>
            <Button appearance="secondary" onClick={() => setFlyoutOpen(false)} disabled={isFormLoading}>Cancel</Button>
            <Button appearance="primary" type="submit" form="pool-edit-form" disabled={isFormLoading}>
              {isFormLoading ? 'Saving…' : flyoutMode === 'create' ? 'Create Pool' : 'Update Pool'}
            </Button>
          </>
        }
      >
        {(createMut.isError || updateMut.isError) && (
          <Text className={styles.errorBlock}>
            {((createMut.error || updateMut.error) as Error)?.message}
          </Text>
        )}
        <ResourcePoolForm
          formId="pool-edit-form"
          hideActions
          mode={flyoutMode}
          initialData={editing || undefined}
          existingNames={existingNames}
          backendOptions={availableBackendOptions}
          excludedBackends={excludedBackends}
          isLoading={isFormLoading}
          onSubmit={handleFormSubmit}
          onCancel={() => setFlyoutOpen(false)}
        />
      </DetailsFlyout>

      <Dialog open={deleteOpen} onOpenChange={(_, d) => setDeleteOpen(!!d.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirm Delete</DialogTitle>
            <div className={styles.deleteGrid}>
              <Text>Delete resource pool:</Text>
              <Text weight="semibold">{target?.name}</Text>
              <Text>This action cannot be undone.</Text>
            </div>
            {deleteMut.isError && <Text className={styles.errorText}>{getErrorMessage(deleteMut.error)}</Text>}
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDeleteOpen(false)} disabled={deleteMut.isPending}>Cancel</Button>
              <Button appearance="primary" onClick={() => target?.id && deleteMut.mutate(target.id)} disabled={deleteMut.isPending || !target?.id}>
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
